import { AnimatedSection } from "@/components/AnimatedSection";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const steps = [
  {
    number: "01",
    title: "Diagnóstico",
    description: "Analisamos sua situação financeira completa: renda, patrimônio, dívidas, seguros, previdência, tributação e objetivos de vida. Em uma conversa de cerca de 60 minutos, mapeamos onde você está hoje — e onde quer chegar.",
  },
  {
    number: "02",
    title: "Estruturação",
    description: "Construímos um plano estratégico sob medida — conectando investimentos, proteção, previdência, gestão de despesas e sucessão em uma única estrutura. Organizamos seu fluxo de caixa para que sobre mais para investir sem comprometer seu padrão de vida. Você recebe um panorama claro de como cada decisão impacta o seu objetivo de longo prazo.",
  },
  {
    number: "03",
    title: "Execução",
    description: "Colocamos o plano em prática — junto com você. Cuidamos da implementação para que você possa focar no que faz de melhor: trabalhar e viver. Sem preocupação. Sem decisões no escuro.",
  },
  {
    number: "04",
    title: "Acompanhamento",
    description: "Sua vida muda, seu plano acompanha. Revisamos a estratégia periodicamente e estamos disponíveis para qualquer decisão patrimonial ou financeira que surja no caminho. Consultoria de verdade não termina na venda — ela começa depois.",
  },
];

const ProcessTimeline = () => {
  return (
    <section id="como-funciona" className="pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-10 md:pb-12 bg-[#FAFAFA]">
      <div className="container-wide">
        {/* Centered Title */}
        <AnimatedSection className="text-center mb-12 sm:mb-16">
          <span className="text-sm text-[#112711]/60 mb-3 block">Como funciona</span>
          <div className="w-12 h-1 bg-[#112711] mb-6 mx-auto" />
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-serif font-medium text-[#112711] leading-tight"
            style={{ textShadow: '0 0 30px rgba(17, 39, 17, 0.4), 0 0 60px rgba(17, 39, 17, 0.2)' }}
          >
            Nosso <span className="font-bold">processo</span>
          </h2>
        </AnimatedSection>

        {/* Timeline */}
        <div className="relative max-w-6xl mx-auto">
          {/* Timeline Line - Desktop */}
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-0.5 bg-[#112711]/30" />
          
          {/* Timeline Points and Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group relative flex flex-col items-center">
                  {/* Timeline Point with Pulse Animation */}
                  <motion.div 
                    className="relative z-10 mb-6 flex items-center justify-center"
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                  >
                    {/* Pulse ring animation */}
                    <motion.div 
                      className="absolute w-16 h-16 rounded-full bg-[#112711]/20"
                      animate={{ 
                        scale: [1, 1.4, 1],
                        opacity: [0.4, 0, 0.4]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3,
                        ease: "easeInOut"
                      }}
                    />
                    {/* Secondary pulse ring */}
                    <motion.div 
                      className="absolute w-16 h-16 rounded-full bg-[#112711]/10"
                      animate={{ 
                        scale: [1, 1.6, 1],
                        opacity: [0.3, 0, 0.3]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3 + 0.3,
                        ease: "easeInOut"
                      }}
                    />
                    {/* Main circle */}
                    <div 
                      className="w-16 h-16 rounded-full bg-[#112711] flex items-center justify-center"
                      style={{ 
                        boxShadow: '0 0 20px rgba(17, 39, 17, 0.4), 0 0 40px rgba(17, 39, 17, 0.2)' 
                      }}
                    >
                      <span 
                        className="text-lg font-serif font-medium text-[#F3DE2C]"
                        style={{ textShadow: '0 0 10px rgba(243, 222, 44, 0.5)' }}
                      >
                        {step.number}
                      </span>
                    </div>
                  </motion.div>
                  
                  {/* Vertical connector line - Mobile */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden absolute top-20 left-1/2 w-0.5 h-8 bg-[#112711]/30 -translate-x-1/2" />
                  )}
                  
                  {/* Card */}
                  <div 
                    className="w-full p-5 rounded-xl bg-white border-2 border-[#112711]/20 text-center"
                    style={{ 
                      boxShadow: '0 0 20px rgba(17, 39, 17, 0.1), 0 4px 12px rgba(17, 39, 17, 0.08)' 
                    }}
                  >
                    <h3 
                      className="text-lg sm:text-xl font-serif font-medium text-[#112711] mb-3"
                      style={{ textShadow: '0 0 15px rgba(17, 39, 17, 0.2)' }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#112711]/80 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* CTA Card */}
        <AnimatedSection delay={0.4}>
          <div className="max-w-xl mx-auto mt-12">
            <Link 
              to="/contato"
              className="group block p-4 rounded-xl bg-[#112711]/85 hover:bg-[#112711] transition-colors duration-300"
              style={{ boxShadow: '0 0 12px rgba(243, 222, 44, 0.15), inset 0 0 0 1px rgba(243, 222, 44, 0.2)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span 
                    className="text-xl sm:text-2xl font-serif font-medium text-[#F3DE2C]" 
                    style={{ textShadow: '0 0 15px rgba(243, 222, 44, 0.5), 0 0 30px rgba(243, 222, 44, 0.3)' }}
                  >
                    Inicie seu planejamento
                  </span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#F3DE2C]/80 group-hover:text-[#F3DE2C] group-hover:translate-x-1 transition-all duration-300" />
              </div>
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ProcessTimeline;
